import type { IdentityAction, IdentityInput } from '@tabel/shared/personnel';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Info } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Button, Callout, Field, SidePanel, inputCls, textareaCls } from '../../components/ui';
import {
  type Decision,
  type Envelope,
  type Group,
  type Page,
  type Person,
  type Preview,
  type Registration,
  dateTime,
  directoryRequest,
} from '../../lib/personnel-api';
import { RegistrationDetail } from './PersonPanel';
import {
  DateFields,
  ErrorNotice,
  Loading,
  PanelSection,
  PreviewCommit,
  StateBadge,
  nameReason,
  useNow,
} from './parts';

export function ReviewPanel({
  id,
  manual = false,
  personIds: initialPeople = [],
  registrations: initialRegistrations = [],
  queueRevision,
  validThroughMs,
  queueReady = true,
  canCorrect = false,
  onClose,
  onChanged,
}: {
  id: string;
  manual?: boolean;
  personIds?: string[];
  registrations?: Registration[];
  queueRevision?: string;
  validThroughMs?: number;
  queueReady?: boolean;
  canCorrect?: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const group = useQuery({
    queryKey: ['personnel', 'group', id, queueRevision],
    queryFn: () =>
      directoryRequest<Group>(
        `/api/personnel/review/groups/${encodeURIComponent(id)}${queueRevision ? `?queueRevision=${encodeURIComponent(queueRevision)}` : ''}`,
      ),
    enabled: !manual && queueReady,
  });
  const priorDecisions = useQuery({
    queryKey: ['personnel', 'manual-decisions', initialPeople[0]],
    queryFn: () =>
      directoryRequest<Page<Decision>>(
        `/api/personnel/decisions${initialPeople[0] ? `?personId=${encodeURIComponent(initialPeople[0])}` : ''}`,
      ),
    enabled: manual,
  });
  const [restoreDecisionId, setRestoreDecisionId] = useState('');
  const [extraRegistrations, setExtraRegistrations] = useState<Registration[]>([]);
  const [extraPeople, setExtraPeople] = useState<Person[]>([]);
  const [personSearch, setPersonSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Person[]>([]);
  const [selected, setSelected] = useState<string[] | null>(null);
  const [selectedPeople, setSelectedPeople] = useState<string[]>(initialPeople);
  const [moveMemberships, setMoveMemberships] = useState(false);
  const [action, setAction] = useState<IdentityAction>('same_person');
  const [target, setTarget] = useState(initialPeople[0] ?? '');
  const [basis, setBasis] = useState('');
  const [from, setFrom] = useState(() => new Date(Date.now() - 30 * 86_400_000).toISOString().slice(0, 16));
  const [to, setTo] = useState('');
  const [unboundedFrom, setUnboundedFrom] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<Envelope<Preview> | null>(null);
  const [commitInput, setCommitInput] = useState<IdentityInput | null>(null);
  const [commitSummary, setCommitSummary] = useState<ReactNode>(null);
  const [openedReview, setOpenedReview] = useState<{ id: string; scopeKey: string } | null>(null);
  const [commitReviewId, setCommitReviewId] = useState('');
  const [commitContextKey, setCommitContextKey] = useState('');
  const registrations = [
    ...(manual ? initialRegistrations : (group.data?.data.registrations ?? [])),
    ...extraRegistrations,
  ].filter((r, index, all) => all.findIndex((other) => other.id === r.id) === index);
  const picked = selected ?? registrations.map((r) => r.id);
  const siteLabel = (registration: Registration | undefined) =>
    registration?.siteName ?? registration?.siteId ?? 'Площадка не указана';
  const people = [
    ...new Set([
      ...selectedPeople,
      ...registrations.filter((r) => picked.includes(r.id)).map((r) => r.initialPersonId),
    ]),
  ];
  const allPeople = [...new Set([...people, ...extraPeople.map((p) => p.id)])];
  const targetId = allPeople.includes(target) ? target : (allPeople[0] ?? '');
  const merging = action === 'merge_persons';
  const needsRange = !merging && action !== 'split';
  const now = useNow();
  const expired = !manual && validThroughMs !== undefined && now > validThroughMs;
  const range = {
    fromMs: unboundedFrom ? null : Date.parse(`${from}:00Z`),
    toMs: to ? Date.parse(`${to}:00Z`) : null,
  };
  const reviewScopeKey = JSON.stringify({
    origin: manual ? 'manual' : 'queue',
    sourceId: id,
    queueRevision,
    registrationIds: [...picked].sort(),
    personIds: [...(merging ? allPeople : people)].sort(),
    action,
    targetId,
    range: needsRange ? range : null,
  });
  const previewContextKey = JSON.stringify({ reviewScopeKey, basis, restoreDecisionId, moveMemberships });
  const latestContext = useRef(previewContextKey);
  latestContext.current = previewContextKey;
  useEffect(() => {
    if (commitContextKey !== previewContextKey) {
      setPreview(null);
      setCommitInput(null);
      setCommitSummary(null);
    }
  }, [previewContextKey, commitContextKey]);
  const previewAction = async () => {
    setError(null);
    setBusy(true);
    try {
      const relationCoverage: [string, string][] = [];
      for (let i = 0; i < picked.length; i++)
        for (let j = i + 1; j < picked.length; j++)
          relationCoverage.push([picked[i] as string, picked[j] as string]);
      const input: IdentityInput = {
        action,
        personIds: merging ? allPeople : people,
        registrationIds: merging ? [] : picked,
        basis: basis.trim(),
        ...(action === 'split' ? { restoreDecisionId } : {}),
        ...(merging
          ? {
              survivingPersonId: targetId,
              ...(moveMemberships ? { membershipResolution: { moveToSurvivor: true as const } } : {}),
            }
          : action === 'split'
            ? {}
            : { range, relationCoverage }),
        ...(['same_person', 'correct'].includes(action) ? { targetPersonId: targetId } : {}),
      };
      let currentReview = manual ? id : openedReview?.scopeKey === reviewScopeKey ? openedReview.id : '';
      if (!currentReview) {
        const opened = await directoryRequest<{ id: string }>('/api/personnel/reviews', 'POST', {
          origin: 'queue',
          queueRevision,
          registrationIds: picked,
          personIds: people,
          range,
          returnContext: `/personnel?view=review&group=${encodeURIComponent(id)}`,
        });
        if (latestContext.current !== previewContextKey) return;
        currentReview = opened.data.id;
        setOpenedReview({ id: currentReview, scopeKey: reviewScopeKey });
      }
      const result = await directoryRequest<Preview>(
        `/api/personnel/reviews/${encodeURIComponent(currentReview)}/preview`,
        'POST',
        input,
      );
      if (latestContext.current !== previewContextKey) return;
      setCommitReviewId(currentReview);
      setCommitContextKey(previewContextKey);
      setCommitInput(input);
      const label: Record<IdentityAction, string> = {
        same_person: 'Связать выбранные регистрации с одним человеком',
        different_people: 'Зафиксировать, что выбранные регистрации относятся к разным людям',
        new_person: 'Создать отдельного человека для выбранных регистраций',
        defer: 'Отложить разбор выбранных версий',
        correct: 'Исправить связи на выбранном периоде',
        split: 'Восстановить связи до выбранного решения',
        merge_persons: 'Объединить карточки целиком',
      };
      const personName = (pid: string) =>
        extraPeople.find((p) => p.id === pid)?.displayName ??
        registrations.find((r) => r.initialPersonId === pid)?.names[0] ??
        'Карточка без полученного имени';
      const utc = (ms: number | null, start: boolean) =>
        ms === null
          ? start
            ? 'без ограничения начала'
            : 'без ограничения конца'
          : `${new Date(ms).toISOString().replace('T', ' ').slice(0, 19)} UTC`;
      setCommitSummary(
        <div className="space-y-3">
          <p className="font-semibold">{label[input.action]}</p>
          {(input.targetPersonId || input.survivingPersonId) && (
            <p>
              Сохранить связь с карточкой:{' '}
              <strong>{personName(input.targetPersonId ?? input.survivingPersonId ?? '')}</strong>
            </p>
          )}
          <div>
            <p className="mb-1 text-ink-2">
              {input.action === 'merge_persons' ? 'Карточки людей:' : 'Выбранные регистрации:'}
            </p>
            <ul className="space-y-1">
              {input.action === 'merge_persons'
                ? input.personIds.map((pid) => <li key={pid}>{personName(pid)}</li>)
                : registrations
                    .filter((r) => input.registrationIds.includes(r.id))
                    .map((r) => (
                      <li key={r.id}>
                        <strong>{r.names.join(' / ') || 'Имя не получено'}</strong> · {siteLabel(r)} · №{' '}
                        {r.localPersonId}
                      </li>
                    ))}
            </ul>
          </div>
          {input.range && (
            <p>
              Период связи:{' '}
              <strong>
                {utc(input.range.fromMs, true)} — {utc(input.range.toMs, false)}
              </strong>
              . Конец не включается.
            </p>
          )}
          {input.action === 'merge_persons' && (
            <p>
              Объединяется вся история карточек, включая прежние связи и fallback.
              {input.membershipResolution?.moveToSurvivor
                ? ' История членства переносится в сохраняемую карточку после проверки всех периодов.'
                : ''}
            </p>
          )}
          {input.action === 'split' && (
            <p>
              Основание исходного решения:{' '}
              {priorDecisions.data?.data.items.find((d) => d.id === input.restoreDecisionId)?.input?.basis ??
                'Выбрано в форме разбора'}
            </p>
          )}
          <p>Основание: {input.basis}</p>
        </div>,
      );
      setPreview(result);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const addPerson = async (p: Person) => {
    setError(null);
    try {
      const r = await directoryRequest<Person>(`/api/personnel/people/${encodeURIComponent(p.id)}`);
      setExtraPeople((old) => (old.some((x) => x.id === p.id) ? old : [...old, r.data]));
      setSelectedPeople((old) => [...new Set([...old, p.id])]);
      setExtraRegistrations((old) => [...old, ...r.data.registrations]);
      setSelected(null);
      setSearchResults([]);
    } catch (e) {
      setError(e);
    }
  };
  return (
    <>
      <SidePanel
        open
        label="Сравнение регистраций"
        title={manual ? 'Явный разбор связей' : (group.data?.data.displayName ?? 'Сверка между площадками')}
        onClose={onClose}
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button onClick={onClose}>Закрыть</Button>
            <Button
              variant="primary"
              disabled={
                busy ||
                !basis.trim() ||
                Boolean(expired) ||
                (!manual && !queueReady) ||
                (needsRange && picked.length === 0) ||
                (merging && allPeople.length < 2) ||
                (action === 'split' && !restoreDecisionId) ||
                ((action === 'different_people' || action === 'defer') && picked.length < 2) ||
                (needsRange && !unboundedFrom && !from) ||
                Boolean(group.error)
              }
              onClick={() => void previewAction()}
            >
              {busy ? 'Проверка…' : 'Проверить последствия'}
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          <ErrorNotice error={group.error} retry={() => void group.refetch()} />
          {!manual && !queueReady && (
            <Callout icon={Info}>
              Сверка обновляется или не готова. Выбор сохранён, но предпросмотр и подтверждение по этой
              очереди недоступны до готового результата.
            </Callout>
          )}
          {group.isLoading && !manual && <Loading />}
          {group.data && (
            <div className="flex flex-wrap items-center gap-2">
              <StateBadge state={group.data.data.state} />
              <span className="text-xs text-ink-3">
                {registrations.length} регистраций · {new Set(registrations.map((r) => r.siteId)).size}{' '}
                площадки
              </span>
            </div>
          )}
          <Callout icon={Info}>
            Совпадение ФИО формирует кандидатов. Выберите конкретные регистрации, действие и период. Сведения
            подразделения и должности показаны с происхождением.
          </Callout>
          {expired && (
            <Callout icon={AlertCircle} tone="warn">
              Срок очереди истёк. Обновите группу или продолжите отдельный явный разбор из карточки.
            </Callout>
          )}
          {manual && (
            <PanelSection title="Добавить карточку к разбору">
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    const r = await directoryRequest<Page<Person>>('/api/personnel/people/search', 'POST', {
                      query: personSearch,
                      purpose: 'card',
                    });
                    setSearchResults(r.data.items);
                  } catch (err) {
                    setError(err);
                  }
                }}
              >
                <Field label="Найти существующего человека">
                  <input
                    aria-label="Найти существующего человека"
                    className={inputCls}
                    value={personSearch}
                    onChange={(e) => setPersonSearch(e.target.value)}
                  />
                </Field>
                <Button type="submit" disabled={!personSearch.trim()}>
                  Найти карточку
                </Button>
              </form>
              {searchResults.map((p) => (
                <Button key={p.id} variant="ghost" onClick={() => void addPerson(p)}>
                  {p.displayName ?? p.id}
                </Button>
              ))}
            </PanelSection>
          )}
          <PanelSection title={`Регистрации · выбрано ${picked.length} из ${registrations.length}`}>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setSelected(registrations.map((r) => r.id))}>
                Выбрать все
              </Button>
              <Button size="sm" onClick={() => setSelected([])}>
                Снять выбор
              </Button>
            </div>
            {registrations.map((r) => (
              <div key={r.id} className="space-y-2">
                <label className="flex min-h-10 cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-accent"
                    checked={picked.includes(r.id)}
                    onChange={(e) =>
                      setSelected(e.target.checked ? [...picked, r.id] : picked.filter((v) => v !== r.id))
                    }
                  />
                  {siteLabel(r)} · № {r.localPersonId}
                </label>
                <RegistrationDetail id={r.id} fallback={r} />
              </div>
            ))}
          </PanelSection>
          {group.data?.data.edges.length ? (
            <details>
              <summary className="cursor-pointer text-sm font-medium">Почему записи оказались рядом</summary>
              <ul className="mt-3 space-y-2 text-xs text-ink-2">
                {group.data.data.edges.map((e) => (
                  <li key={`${e.leftId}:${e.rightId}`} className="rounded-control bg-subtle p-3">
                    <p className="mb-1 font-medium">
                      {siteLabel(registrations.find((r) => r.id === e.leftId))} · №{' '}
                      {registrations.find((r) => r.id === e.leftId)?.localPersonId} ↔{' '}
                      {siteLabel(registrations.find((r) => r.id === e.rightId))} · №{' '}
                      {registrations.find((r) => r.id === e.rightId)?.localPersonId}
                    </p>
                    {e.reasons.map(nameReason).join(' · ')}
                    {e.conflicting ? ' · Есть противоречие в ФИО' : ''}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          <PanelSection title="Решение">
            <Field label="Что сделать">
              <select
                className={inputCls}
                aria-label="Что сделать"
                value={action}
                onChange={(e) => {
                  setAction(e.target.value as IdentityAction);
                  setPreview(null);
                }}
              >
                <option value="same_person">Один человек — связать выбранные регистрации</option>
                <option value="different_people">Разные люди — сохранить прямое отрицательное решение</option>
                <option value="new_person">Создать отдельного человека для выбранных регистраций</option>
                <option value="defer">Отложить эти версии для разбора</option>
                {manual && (
                  <>
                    <option value="correct">Исправить связи на выбранном периоде</option>
                    <option value="split">Разделить ошибочно связанные регистрации</option>
                    {canCorrect && <option value="merge_persons">Объединить карточки целиком</option>}
                  </>
                )}
              </select>
            </Field>
            {action === 'split' && (
              <Field label="Восстановить связи до решения">
                <select
                  aria-label="Восстановить связи до решения"
                  className={inputCls}
                  value={restoreDecisionId}
                  onChange={(e) => setRestoreDecisionId(e.target.value)}
                >
                  <option value="">Выберите конкретное ошибочное решение</option>
                  {priorDecisions.data?.data.items
                    .filter((d) =>
                      ['same_person', 'correct', 'merge_persons', 'new_person'].includes(d.action),
                    )
                    .map((d) => (
                      <option key={d.id} value={d.id}>
                        {dateTime(d.createdAtMs)} · {d.input?.basis ?? d.basis ?? d.action}
                      </option>
                    ))}
                </select>
              </Field>
            )}
            {merging ? (
              <Callout tone="warn" icon={AlertCircle}>
                Будут объединены карточки целиком, включая историю и fallback. Периодный алиас этим действием
                не ограничивается. Конфликты аккаунтов и состава блокируют сохранение.
                <label className="mt-3 flex min-h-10 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={moveMemberships}
                    onChange={(e) => setMoveMemberships(e.target.checked)}
                  />
                  Перенести подтверждённую историю состава в сохраняемую карточку
                </label>
              </Callout>
            ) : needsRange ? (
              <>
                <label className="mb-3 flex min-h-10 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={unboundedFrom}
                    onChange={(e) => setUnboundedFrom(e.target.checked)}
                  />
                  Начало периода без ограничения
                </label>
                <DateFields
                  utc
                  from={from}
                  to={to}
                  setFrom={(v) => {
                    setFrom(v);
                    setUnboundedFrom(false);
                  }}
                  setTo={setTo}
                />
              </>
            ) : null}
            {(merging || action === 'same_person' || action === 'correct') && (
              <Field label={merging ? 'Сохранить основную карточку' : 'Целевая карточка человека'}>
                <select
                  className={inputCls}
                  aria-label={merging ? 'Сохранить основную карточку' : 'Целевая карточка человека'}
                  value={targetId}
                  onChange={(e) => setTarget(e.target.value)}
                >
                  {allPeople.map((pid) => (
                    <option key={pid} value={pid}>
                      {extraPeople.find((p) => p.id === pid)?.displayName ??
                        registrations.find((r) => r.initialPersonId === pid)?.names[0] ??
                        pid}{' '}
                      · {pid.slice(0, 8)}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Основание решения">
              <textarea
                className={textareaCls}
                aria-label="Основание решения"
                value={basis}
                onChange={(e) => setBasis(e.target.value)}
                placeholder="Что проверено и почему принято решение"
              />
            </Field>
            <ErrorNotice error={error} />
          </PanelSection>
        </div>
      </SidePanel>
      {preview && commitInput && commitContextKey === previewContextKey && (
        <PreviewCommit
          title="Подтвердить решение по идентичности?"
          preview={preview}
          summary={commitSummary}
          blockedReason={
            !manual && !queueReady
              ? 'Очередь ещё не готова. Дождитесь результата и повторите предпросмотр.'
              : undefined
          }
          input={commitInput}
          endpoint={`/api/personnel/reviews/${encodeURIComponent(commitReviewId)}/decisions`}
          onCancel={() => setPreview(null)}
          onDone={() => {
            setPreview(null);
            onClose();
            onChanged();
          }}
        />
      )}
    </>
  );
}
